import {
  ANGLES_D_OUVERTURE,
  couleurDeBranche,
  type BulleColoree,
  COULEURS,
  FONDS_DE_PROJET,
  FORMES_DE_PROJET,
  PAPIERS_DE_FICHE,
  TRAITS_DE_PROJET,
  TYPES_DE_FICHE,
  type FormeDeProjet,
  type TraitDeProjet,
} from '@nextilt/commun';
import type { Feuille, Maillon } from '../partage/api/types';
import { estUnObjet, SANS_HABIT_DESSINE, type BulleAHabiller, type HabitDessine } from '../partage/objetsDeBulle';

/**
 * Le panneau « Habit » d'un projet (chantier habit-du-projet, ticket 02) : ses choix, pris tels
 * quels dans les listes fermées de `commun/`, jamais recopiés ; le type et le papier par défaut de
 * ses fiches depuis le chantier types-de-fiche (modification 06).
 */
export const CHOIX_DE_L_HABIT = {
  couleurs: COULEURS,
  fonds: FONDS_DE_PROJET,
  formes: FORMES_DE_PROJET,
  traits: TRAITS_DE_PROJET,
  typesDeFiche: TYPES_DE_FICHE,
  papiersDeFiche: PAPIERS_DE_FICHE,
  /** L'angle sur lequel le projet s'ouvre (chantier angle-a-l-ouverture, modification 02). */
  angles: ANGLES_D_OUVERTURE,
} as const;

/**
 * Où l'habit se règle : à l'ordinateur (décision d'Igor du 2026-10-20), dans le projet lui-même,
 * la feuille sans mère. Une étape porte l'habit de son projet sans le régler : changer depuis une
 * étape l'habit de tout le projet surprendrait (choix fait sans Igor, à son test).
 */
export function habitReglable(parent: Maillon | null, auPouce: boolean): boolean {
  return !auPouce && parent === null;
}

/**
 * Ce que la Carte et la Hiérarchie dessinent de l'habit (chantier habit-du-projet, tickets 03 et
 * 04) : la valeur de l'habit du projet, à l'ordinateur ; au téléphone, aucune, il garde son dessin
 * à lui (rien n'y posait la forme ni le trait avant, l'habit ne s'y voit pas).
 */
function dessineALOrdinateur<T>(valeur: T, auPouce: boolean): T | undefined {
  return auPouce ? undefined : valeur;
}

/** La forme des bulles que la Carte et la Hiérarchie dessinent (ticket 03). */
export function formeDessinee(forme: FormeDeProjet, auPouce: boolean): FormeDeProjet | undefined {
  return dessineALOrdinateur(forme, auPouce);
}

/** Le trait du contour des bulles que la Carte et la Hiérarchie dessinent (ticket 04). */
export function traitDessine(trait: TraitDeProjet, auPouce: boolean): TraitDeProjet | undefined {
  return dessineALOrdinateur(trait, auPouce);
}

/**
 * **L'habit que la Carte et la Hiérarchie dessinent pour chaque bulle** (chantier habit-des-bulles,
 * modification 03) : sa forme et son trait à elle passent devant ceux du projet ; sans réglage,
 * ceux du projet, exactement comme avant. La bulle du projet porte ceux du projet. Sa couleur est
 * celle de sa branche, par la règle de `commun/` (`couleurDeBranche`), appelée sur les bulles de
 * la feuille (et celles que la vue lit en plus, `lire` : les feuilles dépliées de la Hiérarchie),
 * avec ce que ses mères au-dessus lui donnent (`couleurHeritee` de la feuille) : jamais recalculée
 * ici. Au téléphone, rien ne change : aucun habit.
 */
export function habitsDessines(
  donnees: Pick<Feuille, 'bulle' | 'enfants' | 'carte' | 'habit'>,
  auPouce: boolean
): (bulle: BulleAHabiller, lire?: (id: string) => BulleColoree | undefined) => HabitDessine {
  const bulles: BulleColoree[] = [donnees.bulle, ...donnees.enfants, ...donnees.carte.dessous];
  const parId = new Map(bulles.map((b) => [b.id, b]));
  return (bulle, lire) => {
    if (auPouce) return SANS_HABIT_DESSINE;
    const sienne = bulle.parentId !== null;
    const forme = (sienne ? bulle.forme : null) ?? donnees.habit.forme;
    const trait = (sienne ? bulle.trait : null) ?? donnees.habit.trait;
    const lue = (id: string) => (id === bulle.id ? bulle : (parId.get(id) ?? lire?.(id)));
    const couleur = couleurDeBranche(bulle.id, lue, donnees.bulle.couleurHeritee);
    return estUnObjet(forme) ? { objet: forme, trait, couleur } : { forme, trait, couleur };
  };
}
